import { ErrorSvg } from "~/ui-rtk/components/ui/svg/essentials"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button } from "~/ui-rtk/components/ui/controls"
import type { ValidationError } from "~/ui-rtk/api/ingestionApi"

type ValidationErrorDialogProps = {
  isOpen: boolean
  onClose: () => void
  errors: ValidationError[]
  ingestionHistoryId?: string
}

export default function ValidationErrorDialog({
  isOpen,
  onClose,
  errors,
  ingestionHistoryId,
}: ValidationErrorDialogProps) {
  // Group errors by type
  const errorsByCode = errors.reduce(
    (acc, error) => {
      if (!acc[error.code]) {
        acc[error.code] = []
      }
      acc[error.code].push(error)
      return acc
    },
    {} as Record<string, ValidationError[]>,
  )

  const getErrorTitle = (code: string): string => {
    switch (code) {
      case "MISSING_COLUMN":
        return "Missing Required Columns"
      case "INVALID_TYPE":
        return "Invalid Data Types"
      case "MISSING_VALUE":
        return "Missing Required Values"
      case "INVALID_GZIP":
        return "Could Not Read Compressed File"
      default:
        return "Validation Errors"
    }
  }

  return (
    <Dialog isOpen={isOpen} handleClose={onClose} width={600}>
      <div className="max-w-full w-full absolute rounded-md py-6 px-10 center-absolute bg-basic-dark-blue shadow-deep border border-system-error-500">
        <div className="flex flex-col items-start gap-4 mb-6">
          <div className="flex items-center gap-3">
            <ErrorSvg size={24} fill="error" />
            <h3 className="text-xl font-semibold text-white">
              CSV Validation Failed
            </h3>
          </div>
          <p className="text-sm text-platinum">
            Please fix the following errors and try uploading again.
          </p>
        </div>

        <div className="space-y-6 max-h-96 overflow-y-auto">
          {Object.entries(errorsByCode).map(([code, codeErrors]) => (
            <div key={code} className="space-y-2">
              <h4 className="text-base font-semibold text-white">
                {getErrorTitle(code)}
              </h4>
              <div className="space-y-1">
                {codeErrors.map((error, index) => (
                  <div
                    key={index}
                    className="p-3 bg-system-error-500/10 border border-system-error-500/30 rounded-lg"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-system-error-400 text-sm">•</span>
                      <div className="flex-1">
                        <p className="text-sm text-white">{error.message}</p>
                        {error.column && (
                          <p className="text-xs text-platinum mt-1">
                            Column:{" "}
                            <span className="font-medium">{error.column}</span>
                            {error.row !== undefined && (
                              <>
                                {" "}
                                • Row:{" "}
                                <span className="font-medium">{error.row}</span>
                              </>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {ingestionHistoryId && (
          <div className="mt-4 p-3 bg-basic-blue/20 border border-basic-blue rounded-lg">
            <p className="text-xs text-platinum">
              Error ID: <span className="font-mono">{ingestionHistoryId}</span>
            </p>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <Button
            variant={{ variant: "solid", color: "red" }}
            onClick={onClose}
            className="min-w-24"
          >
            Close
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
